import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import BasicModal from 'components/common/BasicModal';
import SingleSelect from 'components/common/SingleSelect';
import PrimaryButton from '../Common/Buttons/PrimaryButton';
import SecondaryButton from '../Common/Buttons/SecondaryButton';
import SalesforceTaskDueDatePicker from '../Tasks/SalesforceTaskDueDatePicker';
import { validateSalesforceTaskDueDate } from '../Tasks/salesforceTaskValidation';

// Confirmation modal for Work Queue bulk actions. Renders the pending action's
// input (a picklist, a date picker, or a plain confirmation message) plus
// shared Cancel/Confirm buttons.
const WorkQueueActionModal = ({
	pendingAction,
	actionValue,
	onActionValueChange,
	submitting = false,
	confirmDisabled = false,
	formOptions,
	formOptionsLoading = false,
	unchangedCount = 0,
	onConfirm,
	onClose,
}) => {
	// Early return if no pending action
	if (!pendingAction) {
		return null;
	}

	const { action, selectedIds } = pendingAction;

	const selectOptions =
		action.optionsKey === 'assignable_users'
			? (formOptions?.assignable_users ?? []).map((user) => ({
					label: user.name,
					value: user.sf_user_id_in_client_org,
				}))
			: (formOptions?.[action.optionsKey] ?? []);

	return (
		<BasicModal
			open
			onClose={onClose}
			title={action.title}
			subtitle={`${selectedIds.length} item${selectedIds.length !== 1 ? 's' : ''} selected`}
			maxWidth='xs'
			actions={
				<>
					<SecondaryButton
						label='Cancel'
						onClick={onClose}
						disabled={submitting}
					/>
					<PrimaryButton
						label='Confirm'
						onClick={onConfirm}
						loading={submitting}
						disabled={confirmDisabled}
					/>
				</>
			}
		>
				{/* Select dropdown for select input */}
				{action.input === 'select' && (
					<Box sx={{ minHeight: '38px' }}>
						{formOptionsLoading ? (
							<Box display='flex' justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
								<CircularProgress size={20} />
							</Box>
						) : (
							<SingleSelect
								value={actionValue ?? ''}
								options={selectOptions}
								onChange={onActionValueChange}
								placeholder={action.placeholder ?? 'Select a value'}
								dataTestId='work-queue-action-select'
							/>
						)}
					</Box>
				)}

				{/* Date picker for date input */}
				{action.input === 'date' && (
					<SalesforceTaskDueDatePicker
						value={actionValue}
						onChange={onActionValueChange}
						validation={validateSalesforceTaskDueDate(actionValue)}
						showError={Boolean(actionValue)}
						testId='work-queue-action-date'
					/>
				)}

				{/* Fallback for other input types */}
				{action.input !== 'select' && action.input !== 'date' && (
					<Typography sx={{ fontSize: '13px' }}>
						{action.message(selectedIds.length)}
					</Typography>
				)}

				{unchangedCount > 0 && (
					<Typography
						sx={{
							fontSize: '12px',
							color: 'text.secondary',
							mt: 1.5,
						}}
					>
						{selectedIds.length === 1 && unchangedCount === 1 ? (
							<>The selected task won&apos;t be updated.</>
						) : (
							<>
								{unchangedCount} of {selectedIds.length} selected tasks
								won&apos;t be updated.
							</>
						)}
					</Typography>
				)}
		</BasicModal>
	);
};

WorkQueueActionModal.propTypes = {
	pendingAction: PropTypes.shape({
		action: PropTypes.shape({
			title: PropTypes.string.isRequired,
			input: PropTypes.oneOf(['select', 'date']),
			optionsKey: PropTypes.string,
			placeholder: PropTypes.string,
			message: PropTypes.func,
		}).isRequired,
		selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
	}),
	actionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	onActionValueChange: PropTypes.func.isRequired,
	submitting: PropTypes.bool,
	confirmDisabled: PropTypes.bool,
	formOptions: PropTypes.object,
	formOptionsLoading: PropTypes.bool,
	unchangedCount: PropTypes.number,
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default WorkQueueActionModal;
